/*
 * Internal helper: Do NOT import outside of logger.ts.
 * Purpose: Forward sanitised warn/error events to New Relic Browser logs via the agent's log API.
 */

import { isPlainObject } from 'lodash';

import { DEBUG_LEVEL } from '../../../config/configConstants';
import { getNewRelicBrowserAgent } from '../analytics/newRelicAgentState';
import { splitLogValues, type ExternalLogDestination } from './loggerExternalDestinations';
import { sanitizeLogValue } from './sanitizeLogValue';

/** Copies an Error with a sanitised message and stack so the raw one never reaches the agent. */
const toSanitisedError = (error: Error): Error => {
    const { name, message, stack } = sanitizeLogValue(error) as {
        name: string;
        message?: string;
        stack?: string;
    };
    const sanitised = new Error(message);
    sanitised.name = name;
    sanitised.stack = stack;
    return sanitised;
};

/**
 * For internal logger usage only. Arguments are already sanitised by logger.ts, except the first top-level Error,
 * which the logger preserves for native console rendering, so it is sanitised here before it leaves the browser.
 */
export const forwardWarnOrErrorToNewRelic = (logLevel: number, values: unknown[]) => {
    try {
        const isErrorLevel = logLevel === DEBUG_LEVEL.ERROR;
        if (!isErrorLevel && logLevel !== DEBUG_LEVEL.WARN) return;

        const agent = getNewRelicBrowserAgent();
        if (!agent) return;

        const { message, firstError, rest } = splitLogValues(values);

        const customAttributes: Record<string, unknown> = {};
        const extra: unknown[] = [];
        for (const value of rest) {
            // Context keys become top-level attributes so they are queryable; avoid agent names like `message`, `level`.
            if (isPlainObject(value)) {
                // Copy string keys only, leaving out the logger's internal marker symbol
                for (const [key, attribute] of Object.entries(value as Record<string, unknown>)) {
                    customAttributes[key] = attribute;
                }
                continue;
            }
            if (value !== undefined) extra.push(value);
        }

        if (extra.length) customAttributes.extra = extra;
        const reportedAttributes = { ...customAttributes };
        if (firstError) customAttributes.error = sanitizeLogValue(firstError);

        agent.log(message, { level: isErrorLevel ? 'ERROR' : 'WARN', customAttributes });
        // Errors Inbox only ingests noticeError, not logs. The Error is the reported error itself, so it is not
        // repeated as an attribute.
        if (isErrorLevel) agent.noticeError(firstError ? toSanitisedError(firstError) : message, reportedAttributes);
    } catch {
        // Forwarding is best-effort and must never break app flow.
    }
};

export const newRelicLogDestination: ExternalLogDestination = {
    isLoggingEnabled: () => !!getNewRelicBrowserAgent(),
    forward: forwardWarnOrErrorToNewRelic,
};
